/**
 * lh-cart-gift-wrap.js
 * Lolahome — Embrulho para Presente no carrinho (tema Horizon)
 *
 * Ao marcar a caixa, adiciona o produto real "Embalagem para presente"
 * (variant 34202830503981, R$ 5,00) ao carrinho via /cart/add.js.
 * Ao desmarcar, remove via /cart/change.js.
 *
 * Atualiza o carrinho via o mesmo mecanismo nativo do Horizon (CartLinesUpdateEvent +
 * morph da seção do carrinho, igual ao product-form.js) em vez de recarregar a página —
 * evita o flash/pisca-pisca do reload e mantém o carrinho aberto sem fechá-lo e reabri-lo.
 *
 * Nenhuma mensagem de erro é mostrada ao cliente: se o pedido falhar, a caixa volta a
 * refletir o estado real do carrinho (silenciosamente, só console.error).
 *
 * Ao marcar/desmarcar, qualquer atualização de observação pendente (debounce) é
 * cancelada antes — evita uma corrida onde o /cart/change.js do texto reaplicaria
 * quantity:1 logo depois do remove, fazendo o embrulho "voltar" sozinho.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const NOTE_PROPERTY_KEY = '_Observação';

function getRoot() {
  return document.getElementById('lh-gift-wrap');
}

function getVariantId() {
  const root = getRoot();
  return root ? Number(root.dataset.variantId) : null;
}

function getCheckbox() {
  return document.getElementById('lh-gift-wrap-checkbox');
}

function getTextarea() {
  return document.getElementById('lh-gift-wrap-note');
}

/**
 * Section ids tracked by every cart-items-component on the page (drawer and/or cart
 * page), mirrors the same lookup product-form.js does before an add/change request.
 */
function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

async function fetchFullCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Posts to a cart AJAX endpoint and dispatches CartLinesUpdateEvent so the cart drawer
 * (cart-items-component), the header cart bubble (cart-icon) and the a11y announcement
 * (header-actions) all update themselves — the exact same event contract product-form.js
 * uses for a normal add-to-cart.
 */
async function updateCart({ url, body, action, lines, onError }) {
  body.sections = getCartSectionIds().join(',');

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const parsed = await response.json();

    if (!response.ok || parsed.status || parsed.errors) {
      const message = parsed.message || parsed.errors || 'Erro ao atualizar o carrinho';
      document.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      deferred.reject(new Error(message));
      onError?.();
      return;
    }

    const ajaxCart = await fetchFullCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'lh-cart-gift-wrap',
        itemCount: ajaxCart.item_count,
        sections: parsed.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error('[lh-cart-gift-wrap] Erro ao atualizar:', error);
    deferred.reject(error);
    onError?.();
  }
}

async function addGiftWrap(note) {
  const variantId = getVariantId();
  if (!variantId) return;

  const properties = note ? { [NOTE_PROPERTY_KEY]: note } : {};

  await updateCart({
    url: '/cart/add.js',
    body: { items: [{ id: variantId, quantity: 1, properties }] },
    action: 'add',
    lines: [{ merchandiseId: String(variantId), quantity: 1 }],
    onError: () => {
      const checkbox = getCheckbox();
      if (checkbox) checkbox.checked = false;
    },
  });
}

async function removeGiftWrap() {
  const variantId = getVariantId();
  if (!variantId) return;

  await updateCart({
    url: '/cart/change.js',
    body: { id: variantId, quantity: 0 },
    action: 'remove',
    lines: [{ merchandiseId: String(variantId), quantity: 0 }],
    onError: () => {
      const checkbox = getCheckbox();
      if (checkbox) checkbox.checked = true;
    },
  });
}

async function updateGiftWrapNote(note) {
  const variantId = getVariantId();
  if (!variantId) return;

  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: note ? { [NOTE_PROPERTY_KEY]: note } : {},
      }),
    });
    const parsed = await response.json();
    if (!response.ok || parsed.errors) {
      throw new Error(typeof parsed.errors === 'string' ? parsed.errors : 'Erro ao atualizar o carrinho');
    }
  } catch (error) {
    console.error('[lh-cart-gift-wrap] Erro ao salvar observação:', error);
  }
}

let noteDebounceTimer = null;
function scheduleNoteUpdate(note) {
  clearTimeout(noteDebounceTimer);
  noteDebounceTimer = setTimeout(() => updateGiftWrapNote(note), 800);
}

function cancelPendingNoteUpdate() {
  clearTimeout(noteDebounceTimer);
  noteDebounceTimer = null;
}

let listenersAttached = false;
function attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  document.addEventListener('change', (e) => {
    if (e.target.id !== 'lh-gift-wrap-checkbox') return;
    cancelPendingNoteUpdate();
    if (e.target.checked) {
      const textarea = getTextarea();
      addGiftWrap(textarea ? textarea.value : '');
    } else {
      removeGiftWrap();
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target.id !== 'lh-gift-wrap-note') return;
    scheduleNoteUpdate(e.target.value);
  });
}

attachListeners();
